// React Dependencies
import React, { Component, createRef } from 'react';

// External Dependencies
import MaterialTable, { MTableToolbar } from 'material-table';
import PropTypes from 'prop-types';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { FilterList as FilterIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';
import { connect } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';

// Internal Dependencies
import OverLoading from '../../global/OverLoading';
import apiService from '../../../services.js/apiService';
import { getOutlet } from '../../../actions/admin/outlet';
import { updateModalContent, toggleModal } from '../../../actions/modal';
import { Status } from '../../../common/constants';
import { IsEmpty } from '../../../common/checks';
import MText from '../../common/MText';
import Colours from '../../../styles/colours';

class OutletTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      redirectUrl: null,
      pageSize:    5,
      outlets:     [],
      isLoading:   false,
    };
    this.tableRef = createRef();
    this.defaultStatus = Status.Live;
  }

  componentDidMount() {
    this.fetchOutlets();
  }

  async fetchOutlets() {
    try {
      this.setState({ isLoading: true });
      const outlets = await apiService.getPageOutlets(this.props.pageId);
      this.setState({ outlets });
    } catch (err) {
      toast.error(err.message);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  async performPageOutletDelete(outletId) {
    try {
      this.setState({ isLoading: true });
      await apiService.deletePageOutlet(outletId);

      this.setState((prevState) => {
        const data = prevState.outlets.filter((outlet) => outlet.id !== outletId);
        return { outlets: data };
      }, () => toast.success('Outlet has been deleted'));

      await this.fetchOutlets();
    } catch (err) {
      toast.error(err.message);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  onDeleteOutlet(outletId) {
    // set the content of the modal
    this.props.updateModalContent({
      title:          'Are you sure?',
      content:        'Some existing references (if any) to this outlet, e.g. reviews on the outlet, will be deleted if you decide to proceed.',
      newLineContent: 'Confirm delete outlet?',
      actionButton:   {
        actionText: 'Delete',
        actionType: 'danger',
        action:     () => this.performPageOutletDelete(outletId),
      },
      cancelText:       'Cancel',
      closeAfterAction: true,
    });
    // show the modal
    this.props.toggleModal();
  }

  handleRedirect = (url) => {
    this.setState({ redirectUrl: url });
  }

  render() {
    if (this.state.redirectUrl) { return <Redirect to={this.state.redirectUrl} />; }
    const { isLoading } = this.state;
    return (
      <div>
        {/** The pagination in MaterialTable is not a true pagination; if we provide
         * the entire data set it has a ability to show by pages and pageSize.
         * If we have huge data set we will need an api to return the paginated
         * data set. Refer to /src/components/admin/category/List.js for how that is
         * achieved in functional component.
          */}
        { isLoading ? <OverLoading /> : null }
        <MaterialTable
          tableRef={this.tableRef}
          title=""
          columns={[
            { title: 'ID', field: 'id', width: '10%' },
            {
              title:  'Name',
              field:  'name',
              width:  '10%',
              render: (outlet) => (
                <Link to={`/admin/page/${this.props.pageId}/outlet/${outlet.id}`}>
                  {outlet.name}
                </Link>
              ),
            },
            { title: 'Address', field: 'address', width: '15%' },
            { title: 'Status', field: 'status', width: '10%', defaultFilter: this.defaultStatus, defaultSort: 'desc' },
            { title: 'Postal Code', field: 'postalCode', width: '10%' },
            { title: 'Lat', field: 'lat', width: '10%' },
            { title: 'Lng', field: 'lng', width: '10%' },
            { title: 'Located In', field: 'locatedIn', width: '15%' },
            { title: 'Unit', field: 'unitNo', width: '10%' },
          ]}
          onChangeRowsPerPage={(pageSize) => this.setState({ pageSize })}
          data={this.state.outlets}
          actions={[
            (row) => ({
              icon:    'edit',
              tooltip: 'View/Edit Outlet',
              onClick: (event, outlet) => {
                this.handleRedirect(`/admin/page/${this.props.pageInView.id}/outlet/${outlet.id}`);
              },
              hidden: row.status === Status.Deactivated,
            }),
            (row) => ({
              icon:    'delete',
              tooltip: 'Delete',
              onClick: (event, outlet) => this.onDeleteOutlet(outlet.id),
              hidden:  row.status === Status.Deactivated,
            }),
          ]}
          options={{
            actionsColumnIndex:  -1,
            pageSize:            this.state.pageSize,
            emptyRowsWhenPaging: false,
            search:              true,
          }}
          components={{
            Toolbar: (props) => (
              <div className="w-100 d-flex justify-content-end align-items-center py-2 px-4">
                <MTableToolbar {...props} />
                <FormControl className="ml-3" style={{ position: 'relative', top: -8 }}>
                  <InputLabel className="pb-3">
                    <MText size={15} style={{ position: 'relative', top: -5 }} colour={Colours.Gray2} text="Status" />
                  </InputLabel>
                  <Select
                    name="status_filter"
                    defaultValue={this.defaultStatus}
                    style={{ width: 220 }}
                    IconComponent={FilterIcon}
                    onChange={(e) => {
                      if (!IsEmpty(this.tableRef.current)) {
                        const statusIndex = this.tableRef.current.dataManager.columns.findIndex((col) => col.field === 'status');
                        this.tableRef.current.onFilterChange(statusIndex, e.target.value);
                      }
                    }}
                  >
                    <MenuItem value="">-</MenuItem>
                    <MenuItem value={Status.Live}>{Status.Live}</MenuItem>
                    <MenuItem value={Status.Deactivated}>{Status.Deactivated}</MenuItem>
                  </Select>
                </FormControl>
              </div>
            ),
          }}
        />
      </div>
    );
  }
}

// map props to redux state (use Chrome Redux DevTools to help identify how state are stored)
function mapStateToProps(state) {
  return {
    pageInView: state.admin.page.pageInView,
  };
}

// map the redux action creators to props
const mapActionToProps = { updateModalContent, toggleModal, getOutlet };

OutletTable.propTypes = {
  pageId:     PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  pageInView: PropTypes.shape({
    id:        PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        id:         PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name:       PropTypes.string,
        address:    PropTypes.string,
        postalCode: PropTypes.string,
        lat:        PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        lng:        PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        locatedIn:  PropTypes.string,
        unitNo:     PropTypes.string,
      }),
    ),
  }).isRequired,

  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

// connect(mapStateToProps, actions to component as props)(component)
export default connect(mapStateToProps, mapActionToProps)(OutletTable);
